import React, { useState, useEffect, type ChangeEvent, type FormEvent } from 'react';
import { toast } from 'react-toastify';
import API_ENDPOINTS from '../../api/apiConfig';
import { MOBILE_RE, registerWithOtp, sendOtp } from '../../api/otpService';
import OtpVerifier from '../../components/auth/OtpVerifier';
import AuthModal, {
  AlertIcon,
  EyeIcon,
  EyeOffIcon,
  Spinner,
} from '../../components/auth/AuthModal';
import shotMain from '../../assets/images/banner-image-1.jpg';
import shotSide from '../../assets/images/banner-image-2.jpg';
import shotDot from '../../assets/images/cat-large-item2.jpg';
import type { CityItem, StateItem } from '../../types';

const PANEL_PERKS = [
  'Faster checkout with saved addresses',
  'Order tracking and easy reviews',
  'Your wishlist, always with you',
];

// ─────────────────────────────────────────────────────────────
// Password strength (visual hint only – nothing is enforced)
// ─────────────────────────────────────────────────────────────
const STRENGTH_LABELS = ['Too short', 'Weak', 'Fair', 'Good', 'Strong'];

const getStrength = (pw: string): number => {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8) score += 1;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score += 1;
  if (/\d/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  return score;
};

const StrengthMeter = ({ password }: { password: string }) => {
  if (!password) return null;
  const score = getStrength(password);
  return (
    <div className={`ffa-meter ffa-meter--${score}`} aria-live="polite">
      {[1, 2, 3, 4].map((n) => (
        <span key={n} className={`ffa-meter-bar${n <= score ? ' is-on' : ''}`} />
      ))}
      <span className="ffa-meter-label">{STRENGTH_LABELS[score]}</span>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Registration
// ─────────────────────────────────────────────────────────────
interface RegistrationProps {
  show?: boolean;
  onClose?: () => void;
  switchToLogin?: () => void;
}

interface RegistrationFormData {
  name: string;
  email: string;
  address: string;
  mobile: string;
  password: string;
  state_id: string | number;
  city_id: string | number;
}

type RegStep = 'form' | 'otp';

const Registration = ({ show = false, onClose, switchToLogin }: RegistrationProps) => {
  const [step, setStep] = useState<RegStep>('form');
  const [resendIn, setResendIn] = useState(60);
  const [verifiedMobile, setVerifiedMobile] = useState('');
  const [formData, setFormData] = useState<RegistrationFormData>({
    name: '', email: '', address: '',
    mobile: '', password: '',
    state_id: '', city_id: '',
  });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const [states, setStates] = useState<StateItem[]>([]);
  const [statesLoading, setStatesLoading] = useState(true);
  const [statesError, setStatesError] = useState<string>('');
  const [cities, setCities] = useState<CityItem[]>([]);
  const [cityLoading, setCityLoading] = useState(false);
  const [cityDisabled, setCityDisabled] = useState(true);

  // Reset to form whenever modal opens
  useEffect(() => {
    if (show) { setStep('form'); setError(''); }
  }, [show]);

  // Fetch states on mount
  useEffect(() => {
    setStatesLoading(true);
    setStatesError('');
    fetch(API_ENDPOINTS.GET_STATES, { headers: { 'Content-Type': 'application/json' } })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data.states)) {
          setStates(data.states);
          localStorage.setItem('statesList', JSON.stringify(data.states));
        } else {
          setStates([]);
          setStatesError('Failed to load states.');
        }
      })
      .catch(() => { setStates([]); setStatesError('Failed to load states.'); })
      .finally(() => setStatesLoading(false));
  }, []);

  // Fetch cities when state changes
  useEffect(() => {
    setFormData(prev => ({ ...prev, city_id: '' }));
    if (!formData.state_id) {
      setCities([]); setCityDisabled(true); return;
    }
    const fetchCities = async () => {
      setCityDisabled(true); setCityLoading(true);
      try {
        const res = await fetch(`${API_ENDPOINTS.GET_CITY}?state_id=${formData.state_id}`, {
          headers: { 'Content-Type': 'application/json' },
        });
        const data = await res.json();
        const list = Array.isArray(data.cities) ? data.cities : Array.isArray(data.data) ? data.data : [];
        setCities(list);
        localStorage.setItem('citiesList', JSON.stringify(list));
      } catch {
        setCities([]);
      } finally {
        setCityDisabled(false); setCityLoading(false);
      }
    };
    fetchCities();
  }, [formData.state_id]);

  if (!show) return null;

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toastErr = (msg: string) =>
    toast.error(msg, { position: 'top-center', autoClose: 3000 });

  // Step 1 — validate form + send OTP
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const mobile = formData.mobile.replace(/\D/g, '');
    if (!formData.name.trim() || !formData.email.trim() || !formData.address.trim() ||
        !mobile || !formData.password.trim() || !formData.city_id || !formData.state_id) {
      setError('All fields are required.');
      toastErr('All fields are required.');
      return;
    }
    if (!MOBILE_RE.test(mobile)) {
      setError('Enter a valid 10-digit mobile number.');
      return;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setError(''); setLoading(true);
    const res = await sendOtp(mobile, 'REGISTER');
    setLoading(false);
    if (res.ok) {
      setFormData(prev => ({ ...prev, mobile }));
      setVerifiedMobile(mobile);
      setResendIn(res.data.resend_in ?? 60);
      setStep('otp');
      toast.success(res.message, { position: 'top-center', autoClose: 2500 });
    } else {
      setError(res.message);
      toastErr(res.message);
    }
  };

  // Step 2 — OTP verified → create account → auto login
  const handleVerified = async (otpToken: string) => {
    const res = await registerWithOtp({
      pu_name: formData.name.trim(),
      pu_email: formData.email.trim(),
      pud_address: formData.address.trim(),
      pu_contact_no: verifiedMobile,
      password: formData.password,
      state_id: formData.state_id,
      city_id: formData.city_id,
      otp_token: otpToken,
    });
    if (res.ok) {
      toast.success(res.message || 'Account created!', { position: 'top-center', autoClose: 2000 });
      if (res.data.access_token) {
        localStorage.setItem('authToken', res.data.access_token);
        setTimeout(() => { if (onClose) onClose(); window.location.reload(); }, 1500);
      } else {
        setTimeout(() => { if (switchToLogin) switchToLogin(); if (onClose) onClose(); }, 1500);
      }
      return;
    }
    setStep('form');
    setError(res.message);
    toastErr(res.message);
  };

  return (
    <AuthModal
      onClose={() => { if (onClose) onClose(); }}
      gallery={[shotMain, shotSide, shotDot]}
      panelEyebrow="Join the house"
      panelTitle={<>Your wardrobe, <em>reimagined</em>.</>}
      perks={PANEL_PERKS}
      labelledBy="ffa-register-title"
      wide
    >
      {step === 'otp' ? (
        <OtpVerifier
          mobile={verifiedMobile}
          purpose="REGISTER"
          initialResendIn={resendIn}
          onVerified={handleVerified}
          onBack={() => { setStep('form'); setError(''); }}
          backLabel="Edit details"
          busyLabel="Creating account…"
        />
      ) : (
      <>
      <p className="ffa-eyebrow">New account</p>
      <h2 id="ffa-register-title" className="ffa-title">Create your <em>account</em></h2>
      <p className="ffa-subtitle">Join Fashion Frame — it only takes a minute.</p>

      {error && (
        <div className="ffa-alert" role="alert">
          <AlertIcon />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} autoComplete="off">

        {/* ── Personal info ── */}
        <div className="ffa-section ffa-section--first">Your details</div>

        <div className="ffa-field">
          <label className="ffa-label" htmlFor="ffa-reg-name">Full name</label>
          <input
            id="ffa-reg-name"
            className="ffa-input"
            type="text"
            name="name"
            placeholder="Your full name"
            value={formData.name}
            onChange={handleChange}
            autoComplete="off"
            required
          />
        </div>

        <div className="ffa-grid-2">
          <div className="ffa-field">
            <label className="ffa-label" htmlFor="ffa-reg-email">Email</label>
            <input
              id="ffa-reg-email"
              className="ffa-input"
              type="email"
              name="email"
              placeholder="you@example.com"
              value={formData.email}
              onChange={handleChange}
              autoComplete="off"
              required
            />
          </div>

          <div className="ffa-field">
            <label className="ffa-label" htmlFor="ffa-reg-mobile">Mobile</label>
            <input
              id="ffa-reg-mobile"
              className="ffa-input"
              type="tel"
              inputMode="tel"
              name="mobile"
              placeholder="10-digit mobile number"
              maxLength={10}
              value={formData.mobile}
              onChange={handleChange}
              autoComplete="off"
              required
            />
          </div>
        </div>

        <div className="ffa-field">
          <label className="ffa-label" htmlFor="ffa-reg-password">Password</label>
          <div className="ffa-input-wrap">
            <input
              id="ffa-reg-password"
              className="ffa-input"
              type={showPw ? 'text' : 'password'}
              name="password"
              placeholder="Create a password"
              value={formData.password}
              onChange={handleChange}
              autoComplete="new-password"
              required
            />
            <button
              type="button"
              className="ffa-eye"
              onClick={() => setShowPw((p) => !p)}
              aria-label={showPw ? 'Hide password' : 'Show password'}
              tabIndex={-1}
            >
              {showPw ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>
          <StrengthMeter password={formData.password} />
          <div className="ffa-hint">Minimum 6 characters. We'll verify your mobile with an OTP.</div>
        </div>

        {/* ── Address ── */}
        <div className="ffa-section">Delivery address</div>

        <div className="ffa-field">
          <label className="ffa-label" htmlFor="ffa-reg-address">Street address</label>
          <input
            id="ffa-reg-address"
            className="ffa-input"
            type="text"
            name="address"
            placeholder="House / flat / street"
            value={formData.address}
            onChange={handleChange}
            autoComplete="off"
            required
          />
        </div>

        <div className="ffa-grid-2">
          <div className="ffa-field">
            <label className="ffa-label" htmlFor="ffa-reg-state">State</label>
            <select
              id="ffa-reg-state"
              className="ffa-select"
              name="state_id"
              value={formData.state_id}
              onChange={handleChange}
              required
              disabled={statesLoading}
            >
              <option value="">{statesLoading ? 'Loading…' : 'Select state'}</option>
              {states.map((s) => (
                <option key={s.state_id} value={s.state_id}>{s.state_name}</option>
              ))}
            </select>
            {statesError && <div className="ffa-field-error" role="alert">{statesError}</div>}
          </div>

          <div className="ffa-field">
            <label className="ffa-label" htmlFor="ffa-reg-city">City</label>
            <select
              id="ffa-reg-city"
              className="ffa-select"
              name="city_id"
              value={formData.city_id}
              onChange={handleChange}
              required
              disabled={cityDisabled}
            >
              <option value="">{cityLoading ? 'Loading…' : 'Select city'}</option>
              {cities.map((c) => (
                <option key={c.city_id} value={c.city_id}>{c.city_name}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ marginTop: 10 }}>
          <button type="submit" className="ffa-btn" disabled={loading}>
            {loading ? <><Spinner /> Sending OTP…</> : 'Continue — verify mobile'}
          </button>
        </div>

        <p className="ffa-footer">
          Already a member?{' '}
          <button type="button" className="ffa-link" onClick={() => { if (switchToLogin) switchToLogin(); }}>
            Sign in
          </button>
        </p>
      </form>
      </>
      )}
    </AuthModal>
  );
};

export default Registration;